import { ReactNode } from "react";
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TextField,
    Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { PageBreadcrumb, PageBreadcrumbItem } from "../page-breadcrumb/page-breadcrumb";
import "./admin-crud.css";

type AdminCrudHeaderProps = {
    title: string;
    subtitle: string;
    addLabel?: string;
    addIcon?: ReactNode;
    onAdd?: () => void;
    breadcrumbItems?: PageBreadcrumbItem[];
};

type AdminCrudSearchProps = {
    value: string;
    placeholder: string;
    onChange: (value: string) => void;
};

type AdminCrudFormDialogProps = {
    open: boolean;
    title: string;
    closeLabel: string;
    cancelLabel: string;
    saveLabel: string;
    saving?: boolean;
    onClose: () => void;
    onSave: () => void;
    children: ReactNode;
};

type AdminCrudDeleteDialogProps = {
    open: boolean;
    title: string;
    message: ReactNode;
    cancelLabel: string;
    confirmLabel: string;
    deleting?: boolean;
    onClose: () => void;
    onConfirm: () => void;
    warning?: string;
    disabled?: boolean;
    children?: ReactNode;
};

type AdminCrudAlertProps = {
    error?: string | null;
    success?: string | null;
    onClearError?: () => void;
    onClearSuccess?: () => void;
};

export const AdminCrudHeader = ({
    title,
    subtitle,
    addLabel,
    addIcon,
    onAdd,
    breadcrumbItems
}: AdminCrudHeaderProps) => (
    <>
        {breadcrumbItems && <PageBreadcrumb items={breadcrumbItems} />}
        <div className="admin-crud-header">
            <div>
                <h1>{title}</h1>
                <p>{subtitle}</p>
            </div>

            {addLabel && onAdd && (
                <Button
                    variant="contained"
                    startIcon={addIcon}
                    onClick={onAdd}
                    className="admin-crud-primary-button"
                >
                    {addLabel}
                </Button>
            )}
        </div>
    </>
);

export const AdminCrudSearch = ({ value, placeholder, onChange }: AdminCrudSearchProps) => (
    <div className="admin-crud-toolbar">
        <SearchIcon className="admin-crud-toolbar-icon" />
        <TextField
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder={placeholder}
            size="small"
            fullWidth
        />
    </div>
);

export const AdminCrudAlerts = ({
    error,
    success,
    onClearError,
    onClearSuccess
}: AdminCrudAlertProps) => (
    <>
        {error && (
            <Alert severity="error" onClose={onClearError} className="admin-crud-alert">
                {error}
            </Alert>
        )}

        {success && (
            <Alert severity="success" onClose={onClearSuccess} className="admin-crud-alert">
                {success}
            </Alert>
        )}
    </>
);

export const AdminCrudFormDialog = ({
    open,
    title,
    closeLabel,
    cancelLabel,
    saveLabel,
    saving = false,
    onClose,
    onSave,
    children
}: AdminCrudFormDialogProps) => (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle className="admin-crud-dialog-title">
            {title}
            <IconButton aria-label={closeLabel} onClick={onClose} disabled={saving}>
                <CloseIcon />
            </IconButton>
        </DialogTitle>
        <DialogContent className="admin-crud-form">
            {children}
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose} disabled={saving}>{cancelLabel}</Button>
            <Button
                variant="contained"
                onClick={onSave}
                disabled={saving}
                className="admin-crud-primary-button"
            >
                {saveLabel}
            </Button>
        </DialogActions>
    </Dialog>
);

export const AdminCrudDeleteDialog = ({
    open,
    title,
    message,
    cancelLabel,
    confirmLabel,
    deleting = false,
    onClose,
    onConfirm,
    warning,
    disabled = false,
    children
}: AdminCrudDeleteDialogProps) => (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent className="admin-crud-delete-dialog">
            <Typography>
                {message}
            </Typography>
            {warning && <Alert severity="warning">{warning}</Alert>}
            {children}
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose} disabled={deleting}>{cancelLabel}</Button>
            <Button
                onClick={onConfirm}
                color="error"
                variant="contained"
                disabled={deleting || disabled}
            >
                {confirmLabel}
            </Button>
        </DialogActions>
    </Dialog>
);
